import { useCallback, useEffect, useState } from "react";
import AdminAuthGuard from "../components/admin/AdminAuthGuard";
import AdminLayout from "../components/admin/AdminLayout";
import AdminPager from "../components/admin/AdminPager";
import {
  listAdminConsultations,
  patchConsultation,
} from "../lib/api/index";
import { logger } from "../lib/logger";

const STATUSES = ["new", "contacted", "scheduled", "completed", "declined"];

const statusPillStyle = (status) => {
  const palette = {
    new:        { fg: "#9aa1aa", bg: "rgba(154,161,170,0.10)", border: "#3a4049" },
    contacted:  { fg: "#c9a24a", bg: "rgba(201,162,74,0.12)",  border: "#7a6128" },
    scheduled:  { fg: "#6da9ff", bg: "rgba(109,169,255,0.12)", border: "#3863a5" },
    completed:  { fg: "#7adb8e", bg: "rgba(122,219,142,0.12)", border: "#3f8c4f" },
    declined:   { fg: "#e2867e", bg: "rgba(226,134,126,0.12)", border: "#8a3e3a" },
  }[status] || { fg: "#9aa1aa", bg: "rgba(154,161,170,0.10)", border: "#3a4049" };
  return {
    color: palette.fg,
    backgroundColor: palette.bg,
    border: `1px solid ${palette.border}`,
    padding: "2px 8px",
    borderRadius: 12,
    fontFamily: "var(--sf-mono)",
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    display: "inline-block",
  };
};

function Modal({ open, onClose, consultation, onSubmitted }) {
  const [status, setStatus] = useState(consultation?.status || "new");
  const [notes, setNotes] = useState(consultation?.admin_notes || "");
  const [lastContacted, setLastContacted] = useState(consultation?.last_contacted_at || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && consultation) {
      setStatus(consultation.status || "new");
      setNotes(consultation.admin_notes || "");
      setLastContacted(consultation.last_contacted_at || "");
      setErr(null);
    }
  }, [open, consultation]);

  if (!open || !consultation) return null;

  const onSave = async () => {
    // Client-side diff — only send changed fields. If nothing has
    // changed, surface the same "No changes to save." copy the backend
    // would return (avoids the round-trip).
    const initialStatus = consultation.status || "new";
    const initialNotes = consultation.admin_notes || "";
    const initialLC = consultation.last_contacted_at || "";
    const body = {};
    if (status !== initialStatus) body.status = status;
    if (notes !== initialNotes) body.admin_notes = notes;
    if (lastContacted !== initialLC) body.last_contacted_at = lastContacted || null;

    if (Object.keys(body).length === 0) {
      setErr("No changes to save.");
      return;
    }

    setSaving(true); setErr(null);
    try {
      const data = await patchConsultation(consultation.consultation_id, body);
      onSubmitted(data);
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      logger.error("admin_consult_update_failed", { code, detail });
      if (code === 400 && detail === "invalid_status") setErr("Invalid status.");
      else if (code === 400 && detail === "no_fields_to_update") setErr("No changes to save.");
      else if (code === 404) setErr("Consultation not found. The row may have been removed.");
      else setErr("Could not save changes.");
    } finally { setSaving(false); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="admin-consultations-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="sf-card"
        style={{ maxWidth: 620, width: "100%", maxHeight: "92vh", overflowY: "auto" }}
      >
        <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Consultation request</div>
        <h2 className="sf-display" style={{ fontSize: 20, marginBottom: 4 }} data-testid="admin-consult-modal-business">
          {consultation.business}
        </h2>
        <div className="sf-mono sf-muted-text" style={{ fontSize: 12, marginBottom: 6 }}>
          {consultation.name} · {consultation.email}
          {consultation.phone ? ` · ${consultation.phone}` : ""}
        </div>
        <div className="sf-mono sf-muted-text" style={{ fontSize: 11, marginBottom: 18, letterSpacing: "0.06em" }} data-testid="admin-consult-modal-meta">
          BH {consultation.business_health_score ?? "—"}
          {consultation.business_health_label ? ` · ${consultation.business_health_label}` : ""}
          {" · "}source {consultation.source || "—"}
          {" · "}created {consultation.created_at || "—"}
        </div>

        {consultation.goals && (
          <div style={{ marginBottom: 14, fontSize: 13 }}>
            <div className="sf-eyebrow" style={{ marginBottom: 4 }}>Top priorities</div>
            <div className="sf-muted-text" data-testid="admin-consult-modal-goals">{consultation.goals}</div>
          </div>
        )}

        {consultation.preferred_time && (
          <div style={{ marginBottom: 14, fontSize: 13 }}>
            <div className="sf-eyebrow" style={{ marginBottom: 4 }}>Preferred scheduling window</div>
            <div className="sf-muted-text" data-testid="admin-consult-modal-preferred-time">{consultation.preferred_time}</div>
          </div>
        )}

        <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Status</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }} data-testid="admin-consult-status-radios">
          {STATUSES.map((s) => (
            <label key={s} style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="consult-status"
                value={s}
                checked={status === s}
                onChange={() => setStatus(s)}
                style={{ marginRight: 6 }}
                data-testid={`admin-consult-status-${s}`}
              />
              <span style={statusPillStyle(s)}>{s}</span>
            </label>
          ))}
        </div>

        <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Admin notes</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={5}
          className="sf-input"
          style={{ width: "100%", marginBottom: 14, fontFamily: "var(--sf-mono)", fontSize: 13 }}
          data-testid="admin-consult-notes"
        />

        <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Last contacted (ISO 8601, optional)</div>
        <input
          type="text"
          value={lastContacted}
          onChange={(e) => setLastContacted(e.target.value)}
          className="sf-input"
          placeholder="2026-06-19T18:00:00+00:00"
          style={{ width: "100%", marginBottom: 18, fontFamily: "var(--sf-mono)", fontSize: 13 }}
          data-testid="admin-consult-last-contacted"
        />

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="sf-btn-secondary" onClick={onClose} data-testid="admin-consult-cancel">
            Cancel
          </button>
          <button type="button" className="sf-btn" onClick={onSave} disabled={saving} data-testid="admin-consult-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        {err && <div className="sf-mono" style={{ color: "#e2867e", fontSize: 12, marginTop: 10 }} data-testid="admin-consult-modal-error">{err}</div>}
      </div>
    </div>
  );
}

function Inner({ admin }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);

  const fetchPage = useCallback(async (p, s) => {
    setLoading(true); setErr(null);
    try {
      const params = { page: p, page_size: pageSize };
      if (s) params.status_filter = s;
      const data = await listAdminConsultations(params);
      setRows(data.rows);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      logger.error("admin_consult_list_failed", { status: e?.response?.status });
      setErr("Could not load consultations.");
    } finally { setLoading(false); }
  }, [pageSize]);

  useEffect(() => { fetchPage(page, statusFilter); }, [fetchPage, page, statusFilter]);

  const onSubmitted = (updated) => {
    setRows((rs) => rs.map((r) => r.consultation_id === updated.consultation_id ? { ...r, ...updated } : r));
    setActive(null);
  };

  return (
    <AdminLayout admin={admin}>
      <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Consultations</div>
      <h1 className="sf-display" style={{ fontSize: 28, marginBottom: 18 }} data-testid="admin-consultations-headline">
        Consultation pipeline.
      </h1>

      <div className="sf-card" style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid="admin-consultations-filters">
        <label className="sf-eyebrow" style={{ fontSize: 11 }}>Status
          <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                  className="sf-input" style={{ marginLeft: 8 }} data-testid="consultations-filter-status">
            <option value="">all</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <span className="sf-mono sf-muted-text" style={{ marginLeft: "auto", fontSize: 12 }} data-testid="consultations-total">
          {total} total
        </span>
      </div>

      {err && <div className="sf-card" data-testid="admin-consultations-error">{err}</div>}

      <div className="sf-card" style={{ padding: 0 }} data-testid="admin-consultations-table">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--sf-gold)", fontFamily: "var(--sf-mono)", fontSize: 11, letterSpacing: "0.08em" }}>
              <th style={{ padding: 10 }}>BUSINESS</th>
              <th style={{ padding: 10 }}>CONTACT</th>
              <th style={{ padding: 10 }}>BH</th>
              <th style={{ padding: 10 }}>STATUS</th>
              <th style={{ padding: 10 }}>CREATED</th>
              <th style={{ padding: 10 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ padding: 16, color: "var(--sf-muted)" }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: "var(--sf-muted)" }}>No consultations yet.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.consultation_id} style={{ borderTop: "1px solid #22262B" }} data-testid={`admin-consultations-row-${r.consultation_id}`}>
                <td style={{ padding: 10 }}>{r.business}</td>
                <td style={{ padding: 10 }} className="sf-mono">
                  {r.name} · {r.email}
                  {r.phone ? ` · ${r.phone}` : ""}
                </td>
                <td style={{ padding: 10 }} className="sf-mono" data-testid={`admin-consultations-row-bh-${r.consultation_id}`}>
                  {r.business_health_score ?? "—"}
                  {r.business_health_label ? <span className="sf-muted-text" style={{ marginLeft: 6, fontSize: 11 }}>· {r.business_health_label}</span> : null}
                </td>
                <td style={{ padding: 10 }}><span style={statusPillStyle(r.status)} data-testid={`admin-consultations-row-status-${r.consultation_id}`}>{r.status}</span></td>
                <td style={{ padding: 10 }} className="sf-mono sf-muted-text">{r.created_at}</td>
                <td style={{ padding: 10, textAlign: "right" }}>
                  <button type="button" className="sf-btn-secondary" onClick={() => setActive(r)} data-testid={`admin-consultations-row-edit-${r.consultation_id}`}>
                    Update
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminPager
        page={page}
        totalPages={totalPages}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        testIdRoot="admin-consultations-pager"
        testIdPrev="consultations-page-prev"
        testIdNext="consultations-page-next"
      />

      <Modal open={!!active} onClose={() => setActive(null)} consultation={active} onSubmitted={onSubmitted} />
    </AdminLayout>
  );
}

export default function AdminConsultations() {
  return <AdminAuthGuard>{(admin) => <Inner admin={admin} />}</AdminAuthGuard>;
}
